import { UserId } from "@pax/api-contract/common";
import { PresetPermission } from "@pax/api-contract/presets/v1alpha1";
import { DraftCommit, DraftOp, fromBase64, RecordKey, TextPath, toBase64 } from "@pax/draft";
import { z } from "zod";
import { service } from "./endpoints";
import { DraftBase, DraftId, DraftPage, DraftProblem, DraftRevision, PublishedAs } from "./schemas";

export const socketProtocol = `${service.group}/${service.version}` as const;

/**
 * A bare text frame a client sends every `HEARTBEAT_INTERVAL_MS`, and the one it gets back. The
 * runtime answers it without waking the draft session.
 */
export const HEARTBEAT = { ping: "ping", pong: "pong" } as const;
export const HEARTBEAT_INTERVAL_MS = 15_000;
/** How long a session waits for a ping before it closes the socket, as if it had closed at its last one. */
export const HEARTBEAT_SILENCE_MS = 45_000;

/** How often an editor sends its presence while someone else is in the draft, at most. */
export const PRESENCE_INTERVAL_MS = 80;
/** How often an editor sends its presence anyway, so others know it's still there. */
export const PRESENCE_KEEPALIVE_MS = 10_000;
/** How often an editor sends what it has typed while it's typing, at most. */
export const TYPING_INTERVAL_MS = 100;

/**
 * The most one socket message may be, under the 1 MiB a Durable Object takes. A frame bigger than
 * this is sent as `part` frames, which the receiver joins back into it.
 */
export const LARGEST_MESSAGE_BYTES = 900_000;
/** The most a frame sent in parts may be once it's joined. */
export const LARGEST_FRAME_BYTES = 32_000_000;

/** Why the session closed a socket. */
export const DRAFT_CLOSE = {
  /** No heartbeat for `HEARTBEAT_SILENCE_MS`. */
  silent: 4001,
  /** The token isn't good for this draft. */
  unauthorized: 4401,
  /** They were taken off the preset's team. */
  removed: 4403,
  /** The draft was published or discarded. */
  ended: 4410,
} as const;

const ConnectionId = z.string().min(1).max(64);
const FrameId = z.string().min(8).max(64);
const Position = z.tuple([z.number().min(-1000).max(1000), z.number().min(-90).max(90)]);

/**
 * One part of a frame too big for one message. Parts are sent in order, one after the other, each
 * chunk a run of the frame's UTF-8 bytes as base64; the receiver joins them and reads the result
 * as the frame they were cut from.
 */
export const PartFrameSchema = z.strictObject({
  type: z.literal("part"),
  id: FrameId,
  index: z.number().int().nonnegative(),
  count: z.number().int().min(2).max(64),
  chunk: z.base64(),
});
type PartFrame = z.infer<typeof PartFrameSchema>;

/** Room for a part frame's own fields, around its chunk. */
const PART_ENVELOPE_BYTES = 1000;

/** A frame's JSON as the messages to send: itself, or the part frames it's cut into. */
export function messagesOf(json: string): string[] {
  const bytes = new TextEncoder().encode(json);
  if (bytes.length <= LARGEST_MESSAGE_BYTES) return [json];
  const size = Math.floor(((LARGEST_MESSAGE_BYTES - PART_ENVELOPE_BYTES) * 3) / 4);
  const count = Math.ceil(bytes.length / size);
  const id = crypto.randomUUID();
  return Array.from({ length: count }, (_, index) =>
    JSON.stringify({
      type: "part",
      id,
      index,
      count,
      chunk: toBase64(bytes.subarray(index * size, (index + 1) * size)),
    } satisfies PartFrame),
  );
}

/** Joins one sender's parts back into the frames they were cut from. */
export class PartJoiner {
  private joining: { id: string; count: number; chunks: Uint8Array[]; bytes: number } | null = null;

  /** The joined frame's JSON once its last part is in; a problem when parts can't be joined. */
  take(
    part: PartFrame,
  ): { json: string } | { waiting: true } | { problem: string; tooLarge?: true } {
    if (part.index === 0) this.joining = { id: part.id, count: part.count, chunks: [], bytes: 0 };
    const joining = this.joining;
    if (
      !joining ||
      joining.id !== part.id ||
      joining.count !== part.count ||
      joining.chunks.length !== part.index
    ) {
      this.joining = null;
      return { problem: "Parts arrived out of order." };
    }
    const chunk = fromBase64(part.chunk);
    joining.bytes += chunk.length;
    if (joining.bytes > LARGEST_FRAME_BYTES) {
      this.joining = null;
      return { problem: "That frame is too big, even in parts.", tooLarge: true };
    }
    joining.chunks.push(chunk);
    if (joining.chunks.length < joining.count) return { waiting: true };
    this.joining = null;
    const joined = new Uint8Array(joining.bytes);
    let at = 0;
    for (const piece of joining.chunks) {
      joined.set(piece, at);
      at += piece.length;
    }
    return { json: new TextDecoder().decode(joined) };
  }
}

/** Where someone is and what they're in the middle of. It's passed on and never stored. */
export const PresenceSchema = z.strictObject({
  /** Which of the editor's tabs they're on. */
  tab: z.string().max(32),
  /** The map they're editing: `spectator`, or a perspective's id. */
  view: z.string().max(64),
  cursor: Position.nullable(),
  tool: z.string().max(32).nullable(),
  selection: z.array(RecordKey).max(100),
  /** Geometry a gesture is producing: a line being drawn, or the outlines of shapes being moved. */
  gesture: z
    .strictObject({ paths: z.array(z.array(Position).max(5000)).max(40), closed: z.boolean() })
    .nullable(),
  /** The skill they have open. */
  skill: z.string().max(64).nullable(),
  /**
   * The text they're typing in, and their caret and selection in it when their editor says, as Yjs
   * relative positions, so they stay put while others type.
   */
  text: z
    .strictObject({
      key: RecordKey,
      path: TextPath,
      anchor: z.base64().max(1000).optional(),
      head: z.base64().max(1000).optional(),
    })
    .nullable(),
});
export type Presence = z.infer<typeof PresenceSchema>;

/** The first frame a client sends: its token, and the last revision it holds. */
export const AuthenticateFrameSchema = z.strictObject({
  type: z.literal("authenticate"),
  token: z.string().min(1),
  protocol: z.literal(socketProtocol),
  since: DraftRevision.meta({ description: "The last revision the client applied, or 0." }),
});

export const ClientFrameSchema = z.discriminatedUnion("type", [
  AuthenticateFrameSchema,
  /** One finished action, as the records it changed, or what was typed since the last one. */
  z.strictObject({ type: z.literal("commit"), commit: DraftCommit }),
  z.strictObject({ type: z.literal("presence"), state: PresenceSchema }),
  PartFrameSchema,
]);
export type ClientFrame = z.infer<typeof ClientFrameSchema>;

const EntrySchema = z.strictObject({
  value: z.record(z.string(), z.json()).nullable(),
  whole: z.boolean(),
  revisions: z.record(z.string(), DraftRevision),
  replaced: z.record(z.string(), DraftRevision),
});

/** Someone in the draft, on one connection. An assistant is in it for the person who asked it. */
export const PeerSchema = z.strictObject({
  connection: ConnectionId,
  userId: UserId,
  name: z.string().min(1),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  assistant: z.boolean(),
  state: PresenceSchema.nullable(),
});
export type Peer = z.infer<typeof PeerSchema>;

export const ServerFrameSchema = z.discriminatedUnion("type", [
  /** The draft, who the client is, and everyone else in it. What it holds follows. */
  z.strictObject({
    type: z.literal("welcome"),
    draft: z.strictObject({
      id: DraftId,
      base: DraftBase.nullable(),
      page: DraftPage.meta({
        description: "What its page said as it began, which `page:main`'s entry changes.",
      }),
      frozen: z.boolean(),
    }),
    you: z.strictObject({
      connection: ConnectionId,
      userId: UserId,
      permissions: z.array(PresetPermission),
    }),
    revision: DraftRevision,
    peers: z.array(PeerSchema),
  }),
  /**
   * Every record the draft has changed, in parts small enough for a socket, when the client is too
   * far behind to be sent the changes it missed.
   */
  z.strictObject({
    type: z.literal("entries"),
    entries: z.array(z.tuple([RecordKey, EntrySchema])),
    last: z.boolean(),
  }),
  /** Every text anyone has typed into, as its Yjs state, so the client's typing merges with it. */
  z.strictObject({
    type: z.literal("texts"),
    texts: z.array(
      z.strictObject({
        key: RecordKey,
        path: TextPath,
        generation: DraftRevision,
        state: z.base64(),
      }),
    ),
    last: z.boolean(),
  }),
  /** The client holds everything up to this revision, and live changes follow. */
  z.strictObject({ type: z.literal("caughtUp"), revision: DraftRevision }),
  /** A commit the draft took: the same to everyone, its author included. */
  z.strictObject({
    type: z.literal("committed"),
    revision: DraftRevision,
    id: FrameId,
    userId: UserId,
    label: z.string(),
    undo: z.boolean(),
    ops: z.array(DraftOp),
    skipped: z.array(z.strictObject({ key: RecordKey, field: z.string() })).meta({
      description: "Fields an undo or redo left alone, because someone else changed them since.",
    }),
  }),
  /** A commit the draft didn't take, to its author alone. Nothing about the draft changed. */
  z.strictObject({ type: z.literal("refused"), id: FrameId, reason: z.string() }),
  z.strictObject({ type: z.literal("presence"), peer: PeerSchema }),
  z.strictObject({ type: z.literal("left"), connection: ConnectionId }),
  /** The client's roles changed. */
  z.strictObject({ type: z.literal("permissions"), permissions: z.array(PresetPermission) }),
  /** The draft is being published and takes no changes. */
  z.strictObject({ type: z.literal("frozen") }),
  /** Its publish was refused or failed, and it takes changes again. */
  z.strictObject({ type: z.literal("thawed"), problems: z.array(DraftProblem) }),
  /** It was published as this version. The socket closes. */
  z.strictObject({ type: z.literal("published"), ...PublishedAs.shape }),
  /** It was discarded. The socket closes. */
  z.strictObject({ type: z.literal("discarded") }),
  z.strictObject({ type: z.literal("token_expired") }),
  z.strictObject({
    type: z.literal("error"),
    code: z.enum(["invalid_frame", "upgrade_required", "frame_too_large"]),
    message: z.string(),
  }),
  PartFrameSchema,
]);
export type ServerFrame = z.infer<typeof ServerFrameSchema>;
